import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sparkles, ShieldCheck, Trophy, Target, FileCheck, Mail, ArrowRight } from 'lucide-react';

// Public course landing page — shareable, no auth required.
// URL convention: /{coursename} = public landing, /{coursename}/course = the app.
// Use this same structure for every SAA course going forward.
export default function ApexLanding() {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('academy_token') : null;

  const goToCourse = () => {
    if (token) navigate('/apex/course');
    else navigate('/academy/login?next=/apex/course');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="apex-landing-page">
      {/* HERO */}
      <div className="border-b border-gray-800/60 bg-gradient-to-b from-[#151515] to-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-orange-500 text-black">ELECTIVE COURSE</Badge>
            <Badge variant="outline" className="border-gray-700 text-gray-400">Scholar Athlete Academy</Badge>
          </div>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tight mb-4" data-testid="apex-landing-title">
            A.P.E.X. <span className="text-orange-400">by SAA</span>
          </h1>
          <p className="text-2xl text-gray-300 mb-3 max-w-3xl leading-snug">
            Athletic Profile & Endorsement Exchange — <span className="text-white">the sovereign recruiter blueprint.</span>
          </p>
          <p className="text-lg text-gray-400 mb-2 max-w-2xl">
            Turn talent into a durable, marketable, protected career. Built for middle-school and high-school student-athletes and the families who protect them.
          </p>
          <p className="text-sm uppercase tracking-widest text-orange-400 mb-8" data-testid="apex-landing-author">
            Course authored by <span className="text-white">Coach B</span> · Founder, PRSVR / Scholar Athlete Academy
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={goToCourse} className="bg-orange-500 hover:bg-orange-600 text-black font-bold text-base h-12 px-6"
                    data-testid="apex-landing-start-btn">
              {token ? 'Continue Course →' : 'Start the Course →'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/academy/apply')}
                    className="border-gray-700 text-white hover:bg-gray-800 h-12"
                    data-testid="apex-landing-enroll-btn">
              Enroll at SAA
            </Button>
          </div>
        </div>
      </div>

      {/* Trilogy positioning */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <p className="text-center text-gray-500 text-sm mb-6 uppercase tracking-widest">The SAA Trilogy</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <Card className="bg-[#111] border-gray-800/60"><CardContent className="p-4 text-center">
            <div className="text-[11px] text-gray-500 uppercase tracking-widest">Nutrition</div>
            <div className="text-lg font-black text-white">FUEL</div>
            <div className="text-xs text-gray-500">feeds your body</div>
          </CardContent></Card>
          <Card className="bg-[#111] border-gray-800/60"><CardContent className="p-4 text-center">
            <div className="text-[11px] text-gray-500 uppercase tracking-widest">Academic</div>
            <div className="text-lg font-black text-white">SOLO</div>
            <div className="text-xs text-gray-500">powers your mind</div>
          </CardContent></Card>
          <Card className="bg-orange-500/10 border-orange-500/50"><CardContent className="p-4 text-center">
            <div className="text-[11px] text-orange-400 uppercase tracking-widest">NIL & Recruiting</div>
            <div className="text-lg font-black text-white">APEX</div>
            <div className="text-xs text-orange-300">launches your brand</div>
          </CardContent></Card>
        </div>
      </div>

      {/* What's inside */}
      <div className="max-w-5xl mx-auto px-6 pb-12">
        <h2 className="text-2xl font-black mb-6">What's Inside</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {icon: Target, title: 'Module 1 · Identity & Brand', desc: '5-Second Coach Test, on-court vs. off-court marketability, the SAA 5 Principles pitch.'},
            {icon: Mail, title: 'Module 2 · Recruiting Engine', desc: 'Verified metrics, the 15-second film rule, direct outreach mechanics + email template.'},
            {icon: ShieldCheck, title: 'Module 3 · Legalities & Business OS', desc: 'CIF · FHSAA · UIL state compliance, undue-influence traps, LLC/1099/tax basics.'},
            {icon: Trophy, title: 'Module 4 · Decision Matrix', desc: '8-semester rule, transfer portal math, college-fit ranking framework.'},
            {icon: Sparkles, title: 'AI Tool 1 · Bio Optimizer', desc: 'Audits your Instagram/X/Hudl bio and generates an optimized version + pinned strategy.'},
            {icon: Sparkles, title: 'AI Tool 2 · Coach Email', desc: 'Pulls your GPA + IXL Mastery + PER + Hudl and drafts a personalized coach email.'},
            {icon: FileCheck, title: 'Mastery Quiz + Dossier', desc: '100% required. Unlocks the APEX Verified Marketable Athlete badge + PDF Recruiting Dossier.'},
            {icon: ShieldCheck, title: 'COPPA-Safe by Default', desc: 'Under-13 accounts locked to private mode until parents complete $0.01 Stripe verification.'},
          ].map(({icon: Icon, title, desc}, i) => (
            <Card key={i} className="bg-[#111] border-gray-800/60">
              <CardContent className="p-5 flex gap-3">
                <div className="w-10 h-10 rounded bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <div className="font-bold mb-1">{title}</div>
                  <p className="text-sm text-gray-400">{desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-3xl mx-auto px-6 pb-16 text-center">
        <Card className="bg-orange-500/5 border-orange-500/40">
          <CardContent className="p-8">
            <h3 className="text-2xl font-black mb-2">Ready to build your APEX?</h3>
            <p className="text-gray-300 mb-5">4 modules · 12 lessons · 2 AI tools · gated mastery quiz · downloadable recruiting dossier.</p>
            <Button onClick={goToCourse} className="bg-orange-500 hover:bg-orange-600 text-black font-bold text-base h-12 px-8"
                    data-testid="apex-landing-cta-btn">
              {token ? 'Continue Course' : 'Start the Course'} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Legal footer */}
      <div className="border-t border-gray-800/40 py-6">
        <div className="max-w-5xl mx-auto px-6 text-[10px] text-gray-600 leading-relaxed">
          EDUCATIONAL GUIDANCE ONLY — NOT LEGAL ADVICE. High school association bylaws (CIF, FHSAA, UIL) and state laws shift frequently. Always verify agreements with a licensed attorney or your high school athletic director before signing.
        </div>
      </div>
    </div>
  );
}
